import { createStyles, makeStyles, Theme } from '@material-ui/core';
import { Stack, Button, Box, FormControl, MenuItem, Select, TextField, Autocomplete } from '@mui/material';
import React from 'react';
import { ContestApiResponse, LineupApiResponse } from './queries/queryTypes';
import { useGetContests, useGetSites, useGetSports, useGetUsersForContest } from './queries/settingsQueries';
import { StyledSettingsContainer } from './_components';
import { getLineupTabRoi, getPlayerTabRoi, getSlates, getStackTabRoi, getUserRoi, resetUserRoi, setContest, setDate, setEnabledContest, setOpponent, setUser } from './store/actions';
import { connect, useDispatch, useSelector } from 'react-redux';
import { Filter, Filters, Page, Pages, Slate } from './store/types';
import { Constants } from './store/constants';
import DatePicker from './Datepicker';
import { APIState } from '../utils/api/reducer';
import { AuthResponse } from '../preContest/store/types';
import { useHistory, useParams } from 'react-router-dom';
import { stackSports } from '../utils/stackConstants';
import { siteLogos, sportLogos } from '../utils/logos';
import {
  useQueryParams,
  StringParam,
  NumberParam,
} from 'use-query-params';
import UnauthenticatedView from '../common/UnauthenticatedView';
import { userSubscribed } from '../utils/authentication';
import { setSite, setSport } from '../common/store/actions';

const useStyles = makeStyles((theme: Theme) => createStyles({
  formWrapper: {
    width: '100%',
    color: 'white',
    '& .MuiInputBase-root': {
      color: 'white',
      backgroundColor: '#000A16',
      borderRadius: 6,
      padding: 8,
      height: 48,
    },
    '& .MuiSelect-selectMenu': {
      display: 'flex',
    },
    '& .MuiListItem-root.Mui-disabled': {
      color: ' #8F9296',
      opacity: '1 !important'
    },
    '& .MuiFormLabel-root': {
      color: 'white',
    },
    '& .MuiFormControl-root': {
      margin: 0,
    },
    '& .MuiSvgIcon-root': {
      color: 'white',
    },
    '& button': {
      backgroundColor: '#0042C1 !important',
      // padding: 8,
    },
    '& legend': {
      color: 'white',
    }
  },
  subtitle: {
    lineHeight: '130%',
    opacity: '70%',
  },
  advancedButton: {
    background: '#0042C1',
    color: 'white',
    fontFamily: "Inter",
    width: '100%',
  },
  signInButtonWrapper: {
    padding: 8,
  },
  signInButton: {
    width: '100%',
    border: '1px solid #0042C1',
    color: 'white',
    fontFamily: "Inter",
  },
})
);

interface ReduxProps {
  readonly selectedContest: ContestApiResponse;
  readonly selectedUser: string,
  readonly selectedOpponent: string,
  readonly filters: Filters,
  readonly pageInfo: Pages,
  readonly selectedDate: Date | null;
  readonly authenticated: APIState<AuthResponse>;
  readonly slates: APIState<Slate[]>;
  readonly userRoi: APIState<LineupApiResponse[]>,

}

interface DispatchProps {
  SetContest: (contest: ContestApiResponse) => void;
  SetEnabledContest: (contest: ContestApiResponse) => void;
  SetUser: (user: string) => void;
  SetOpponent: (opponent: string) => void;
  GetUserRoi: (siteContestId: string, filter: Filter, pageInfo: Page, user: string, constant: Constants.SET_USER_ROI | Constants.SET_USER_NAME_ROI, headerConstant: any) => void;
  GetPlayerTabRoi: (siteContestId: string, user: string, opponent: string, filter: Filter, pageInfo: Page, constant: Constants.SET_PLAYER_ALL_ROI | Constants.SET_PLAYER_USER_ROI | Constants.SET_PLAYER_OPPONENT_ROI, headerConstant: any) => void;
  GetLineupTabRoi: (siteContestId: string, user: string, filter: Filter, pageInfo: Page, constant: Constants.SET_LINEUP_ALL_ROI | Constants.SET_LINEUP_USER_ROI | Constants.SET_LINEUP_OPPONENT_ROI, headerConstant: any) => void;
  GetStackTabRoi: (siteContestId: string, user: string, filter: Filter, pageInfo: Page, constant: Constants.SET_STACK_ALL_ROI | Constants.SET_STACK_USER_ROI | Constants.SET_STACK_OPP_ROI, headerConstant: any, sport: string) => void;
  SetDate: (date: Date | null) => void;
  ResetUserRoi: () => void;
  SetSlates: (sport: string, site: string, date: Date | null) => void;
}

type Props = ReduxProps & DispatchProps;

const Settings = (props: Props) => {
  const classes = useStyles();
  const {
    selectedContest,
    selectedUser,
    selectedOpponent,
    filters,
    pageInfo,
    selectedDate,
    authenticated,
    slates,
    userRoi,
    SetContest,
    SetEnabledContest,
    SetUser,
    SetOpponent,
    GetUserRoi,
    GetPlayerTabRoi,
    GetLineupTabRoi,
    GetStackTabRoi,
    SetDate,
    ResetUserRoi,
    SetSlates
  } = props;
  const [contestValue, setContestValue] = React.useState(0);
  const [slate, setSlate] = React.useState(0);
  const { selectedSite, selectedSport } = useSelector((state: any) => state.common);
  const dispatch = useDispatch();
  const { isLoading: isLoadingSports, data: sports } = useGetSports();
  const { isLoading: isLoadingSites, data: sites } = useGetSites(selectedSport.sport, selectedSport?.sport === '');
  const [filteredUser, setFilteredUsers] = React.useState([] as string[])
  const [filteredOpponents, setFilteredOpponents] = React.useState([] as string[])
  const { isLoading, data } = useGetContests(slate?.toString());
  const [selectedContestDate, setSelectedContestDate] = React.useState<Date | null>(new Date())
  const { data: users, isLoading: isUsersLoading } = useGetUsersForContest(contestValue.toString());
  const history = useHistory();
  const routeParams = useParams<{ league: string }>();
  const [search, setSearch] = useQueryParams({ site: StringParam, slate: NumberParam });
  const isUserSubscribed = userSubscribed(authenticated, selectedSite, selectedSport);

  const handleSportSelect = (contest: ContestApiResponse) => {
    dispatch(setSport({ sport: contest.sport }));
    history.push(`${contest.sport}`);
    history.go(0);
  }

  const handleUserChange = (search: string) => {
    const filteredUsers = users?.filter(x => x?.toLowerCase().includes(search?.toLowerCase()));
    setFilteredUsers(filteredUsers || [] as string[])
  }

  const handleOpponentChange = (search: string) => {
    const filteredOpponent = users?.filter(x => x?.toLowerCase().includes(search?.toLowerCase()));
    setFilteredOpponents(filteredOpponent || [] as string[])
  }

  const handleSlateSelect = (slate: Slate) => {
    setSlate(slate.id);
  }

  const handleSiteSelect = (newSite: string) => {
    dispatch(setSite({ site: newSite }));
    setSearch({ ...search, site: newSite });
  }

  const handleContestSelect = (contest: ContestApiResponse) => {
    setContestValue(contest.siteContestId);
    SetContest(contest);
    SetUser('');
    SetOpponent('');
  }

  const formatDate = (date: string) => {

    var d = new Date(date);
    d.setHours(d.getHours() - 5)
    var month = '' + (d.getMonth() + 1),
      day = '' + d.getDate(),
      year = d.getFullYear();

    if (month.length < 2)
      month = '0' + month;
    if (day.length < 2)
      day = '0' + day;

    return [month, day, year].join('/');
  }

  const handleSimulate = () => {
    GetUserRoi(selectedContest.siteContestId?.toString(), filters.user, pageInfo.user, '', Constants.SET_USER_ROI, Constants.SET_USER_ROI_PAGINATION);
    GetPlayerTabRoi(selectedContest.siteContestId?.toString(), '', '', filters.player.all, pageInfo.player, Constants.SET_PLAYER_ALL_ROI, Constants.SET_PLAYER_ALL_ROI_PAGINATION);
    GetLineupTabRoi(selectedContest.siteContestId?.toString(), '', filters.lineup.all, pageInfo.lineup, Constants.SET_LINEUP_ALL_ROI, Constants.SET_LINEUP_ALL_ROI_PAGINATION);
    if (stackSports.includes(routeParams.league?.toLowerCase())) {
      GetStackTabRoi(selectedContest.siteContestId.toString(), '', filters.stack.all, pageInfo.stack, Constants.SET_STACK_ALL_ROI, Constants.SET_LINEUP_ALL_ROI_PAGINATION, selectedContest.sport);
    }

    if (selectedUser) {
      GetUserRoi(selectedContest.siteContestId?.toString(), filters.user, { pageSize: 1, currentPage: 1 }, selectedUser, Constants.SET_USER_NAME_ROI, Constants.SET_USER_NAME_ROI_PAGINATION);
      GetPlayerTabRoi(selectedContest.siteContestId?.toString(), selectedUser, selectedOpponent, filters.player.user, pageInfo.player, Constants.SET_PLAYER_USER_ROI, Constants.SET_PLAYER_USER_ROI_PAGINATION);
      GetLineupTabRoi(selectedContest.siteContestId?.toString(), selectedUser, filters.lineup.user, pageInfo.lineup, Constants.SET_LINEUP_USER_ROI, Constants.SET_LINEUP_USER_ROI_PAGINATION);
      if (stackSports.includes(routeParams.league?.toLowerCase())) {
        GetStackTabRoi(selectedContest.siteContestId.toString(), selectedUser, filters.stack.user, pageInfo.stack, Constants.SET_STACK_USER_ROI, Constants.SET_STACK_USER_ROI_PAGINATION, selectedContest.sport);
      }
    }
    if (selectedOpponent) {
      GetPlayerTabRoi(selectedContest.siteContestId?.toString(), selectedOpponent, selectedUser, filters.player.opp, pageInfo.player, Constants.SET_PLAYER_OPPONENT_ROI, Constants.SET_PLAYER_OPPONENT_ROI_PAGINATION);
      GetLineupTabRoi(selectedContest.siteContestId?.toString(), selectedOpponent, filters.lineup.opp, pageInfo.lineup, Constants.SET_LINEUP_OPPONENT_ROI, Constants.SET_LINEUP_OPPONENT_ROI_PAGINATION);
      if (stackSports.includes(routeParams.league?.toLowerCase())) {
        GetStackTabRoi(selectedContest.siteContestId.toString(), selectedOpponent, filters.stack.opp, pageInfo.stack, Constants.SET_STACK_OPP_ROI, Constants.SET_STACK_OPP_ROI_PAGINATION, selectedContest.sport);
      }
    }
    SetEnabledContest(selectedContest);
  };


  React.useEffect(() => {
    if (selectedUser && userRoi.data && userRoi.data.length > 0) {
      GetUserRoi(selectedContest.siteContestId?.toString(), filters.user, { pageSize: 1, currentPage: 1 }, selectedUser, Constants.SET_USER_NAME_ROI, Constants.SET_USER_NAME_ROI_PAGINATION);
      GetPlayerTabRoi(selectedContest.siteContestId?.toString(), selectedUser, selectedOpponent, filters.player.user, pageInfo.player, Constants.SET_PLAYER_USER_ROI, Constants.SET_PLAYER_USER_ROI_PAGINATION);
      GetLineupTabRoi(selectedContest.siteContestId?.toString(), selectedUser, filters.lineup.user, pageInfo.lineup, Constants.SET_LINEUP_USER_ROI, Constants.SET_LINEUP_USER_ROI_PAGINATION);
      if (stackSports.includes(routeParams.league?.toLowerCase())) {
        GetStackTabRoi(selectedContest.siteContestId.toString(), selectedUser, filters.stack.user, pageInfo.stack, Constants.SET_STACK_USER_ROI, Constants.SET_STACK_USER_ROI_PAGINATION, selectedContest.sport);
      }
    }
  }, [selectedUser])

  React.useEffect(() => {
    if (selectedContest.siteContestId) {
      handleSimulate();
    }
  }, [pageInfo, filters])

  React.useEffect(() => {
    if (!isLoadingSports && sports && sports?.length > 0 && !selectedSport.sport) {
      const sport = sports.find(x => x?.sport?.toLowerCase() === routeParams.league?.trim()?.toLowerCase());
      dispatch(setSport({ sport: sport?.sport || 'NBA' }));
    }
  }, [sports, routeParams, isLoadingSports])

  React.useEffect(() => {
    if (!isLoadingSites && sites && sites?.length > 0 && !selectedSite.site) {
      const site = sites.find(x => x.site?.toLowerCase() === search.site?.toLowerCase())?.site || sites[0].site;
      dispatch(setSite({ site: site || '' }));
    }
  }, [sites])

  React.useEffect(() => {
    ResetUserRoi();
  }, [selectedContest]);

  React.useEffect(() => {
    if (selectedSport.sport && selectedDate) {
      SetSlates(selectedSport.sport, selectedSite.site, selectedDate)
    }
  }, [selectedDate, selectedSite.site]);

  React.useEffect(() => {
    if (!isLoading && data && data?.length > 0 && selectedContestDate !== selectedDate) {
      setContestValue(data[0]?.siteContestId || 0)
      SetContest(data[0]);
      setSelectedContestDate(selectedDate)
    }
    if (users) {
      setFilteredUsers(users)
      setFilteredOpponents(users);
    }
  }, [data, users])

  React.useEffect(() => {
    if (slates.data?.length > 0) {
      setSlate(slates.data[0]?.id || 0)
    }
  }, [slates])

  if (!isUserSubscribed) {
    return (
      <UnauthenticatedView title='' app='POSTCONTEST' backgroundColor='rgba(1, 90, 206, 0.15)' />
    )
  }

  return (
    <StyledSettingsContainer>
      <Stack spacing={0.5} padding='7px 20px' direction='row'>
        {
          sports && sports?.length > 0 && (
            <Box>
              <FormControl className={classes.formWrapper}>
                <Select
                  labelId="league-simple-select-outlined-label"
                  id="league-simple-select-outlined"
                  label="League"
                  value={selectedSport.sport}
                  color='info'
                >
                  {
                    sports && sports.map((x: ContestApiResponse, i: number) => (
                      <MenuItem key={x.siteContestId} value={x.sport} onClick={() => handleSportSelect(x)}>
                        <Stack direction='row' alignItems='center'>
                          <Box height="20px" width="24px" marginRight="4px">
                            <img src={sportLogos[x.sport?.toUpperCase()]?.logo} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                          </Box>
                          {`${x.sport}`}
                        </Stack>
                      </MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </Box>
          )
        }
        {
          sites && sites?.length > 0 && (
            <Box>
              <FormControl className={classes.formWrapper}>
                <Select
                  labelId="site-simple-select-outlined-label"
                  id="site-simple-select-outlined"
                  label="Site"
                  value={selectedSite.site}
                >
                  {
                    sites && sites.map((x: ContestApiResponse, i: number) => (
                      <MenuItem key={x.siteContestId} value={x.site} onClick={() => handleSiteSelect(x.site)}>
                        <Stack direction='row' alignItems='center'>
                          <Box height="20px" width="24px" marginRight="4px">
                            <img src={siteLogos[x.site?.toLowerCase()]?.logo} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                          </Box>
                          {`${x.site}`}
                        </Stack>
                      </MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </Box>
          )
        }
        <DatePicker onSetDate={SetDate} site={selectedSite.site} />
        {
          slates.data && slates.data?.length > 0 && (
            <Box>
              <FormControl className={classes.formWrapper}>
                <Select
                  labelId="demo-simple-select-outlined-label"
                  id="demo-simple-select-outlined"
                  label="Age"
                  value={slate}
                >
                  {
                    slates.data && slates.data.map((x: Slate, i: number) => (
                      <MenuItem key={x.id} value={x.id} onClick={() => handleSlateSelect(x)}>{`${x.name} (${formatDate(x.startTime)})`}</MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </Box>
          )
        }
        {
          slates.data?.length === 0 && selectedDate && (
            <Box padding="12px 24px" display='flex' justifyContent='center'>
              No Contests available at this time
            </Box>
          )
        }
        {
          slate !== 0 && data && data?.length > 0 && (
            <>
              <Stack direction='row' columnGap={1} mr={1}>
                <FormControl className={classes.formWrapper}>
                  <Select
                    labelId="demo-simple-select-outlined-label"
                    id="demo-simple-select-outlined"
                    label="Age"
                    value={contestValue}
                  >
                    {
                      data && data.map((x: ContestApiResponse, i: number) => (
                        <MenuItem key={x.siteContestId} value={x.siteContestId} onClick={() => handleContestSelect(x)} disabled={false}>{`${x.name} (${formatDate(x.startTime)})`}</MenuItem>
                      ))
                    }
                  </Select>
                </FormControl>
                <FormControl className={classes.formWrapper} style={{ minWidth: 125}}>
                  <Autocomplete
                    freeSolo
                    disableClearable
                    options={(filteredUser || [])?.slice(0, 2000)}
                    loading={isUsersLoading}
                    loadingText="Loading Users"
                    value={selectedUser}
                    onInputChange={(e: any) => handleUserChange(e?.target?.value || '')}
                    onChange={(e, value: any) => SetUser(value)}
                    size='small'
                    renderInput={(params) => (
                      <TextField
                        style={{minWidth: 125}}
                        {...params}
                        label=""
                        margin="dense"
                        variant="standard"
                        value={selectedUser}
                        InputProps={{ ...params.InputProps, type: 'search' }}
                        placeholder="User"
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                </FormControl>
                <FormControl className={classes.formWrapper} style={{ minWidth: 125}}>
                  <Autocomplete
                    freeSolo
                    disableClearable
                    options={(filteredOpponents || [])?.slice(0, 2000).map((option) => option)}
                    loading={isUsersLoading}
                    loadingText="Loading Users"
                    onInputChange={(e: any) => handleOpponentChange(e?.target?.value || '')}
                    onChange={(e, value: any) => SetOpponent(value?.toLowerCase())}
                    size='small'
                    value={selectedOpponent}
                    renderInput={(params) => (
                      <TextField
                        style={{minWidth: 125}}
                        {...params}
                        label=""
                        margin="normal"
                        variant="standard"
                        value={selectedOpponent}
                        InputProps={{ ...params.InputProps, type: 'search' }}
                        placeholder="Opponent"
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                </FormControl>
              </Stack>
              <Button
                variant='contained'
                color='primary'
                disabled={!selectedContest}
                onClick={() => handleSimulate()}
                sx={{
                  height: 48,
                  padding: 1,
                  ml: '8px !important',
                }}
              >
                View Contest Simulation
              </Button>
            </>
          )
        }

      </Stack>
    </StyledSettingsContainer>
  )
};

const mapStateToProps = (state: any) => {
  const {
    selectedContest,
    selectedUser,
    selectedOpponent,
    filters,
    pageInfo,
    selectedDate,
    slates,
    userRoi,
  } = state.postContest;

  const {
    authenticated
  } = state.common;

  return {
    selectedContest,
    selectedUser,
    selectedOpponent,
    filters,
    pageInfo,
    selectedDate,
    authenticated,
    slates,
    userRoi,
  }
};

const mapDispatchToProps = {
  SetContest: setContest,
  SetEnabledContest: setEnabledContest,
  SetUser: setUser,
  SetOpponent: setOpponent,
  GetUserRoi: getUserRoi,
  GetPlayerTabRoi: getPlayerTabRoi,
  GetLineupTabRoi: getLineupTabRoi,
  SetDate: setDate,
  GetStackTabRoi: getStackTabRoi,
  ResetUserRoi: resetUserRoi,
  SetSlates: getSlates,
};

export default connect(mapStateToProps, mapDispatchToProps)(Settings);